import { range } from "@prismicio/editor-support/Array"
import { groupBy } from "@prismicio/editor-support/Map"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  Badge,
  Box,
  Button,
  Checkbox,
  ComboBox,
  ComboboxAction,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ErrorBoundary,
  Icon,
  type IconName,
  type MediaAssetType,
  Skeleton,
  Text,
  Tooltip
} from "@prismicio/editor-ui"
import { forwardRef, Suspense, useMemo, useState } from "react"

import { useSelectionManager } from "../../SelectionManager"
import { type Tag, useTagSearch } from "../hooks/tagData"
import { MediaLibraryAssetCount } from "./MediaLibraryAssetCount"
import { useMediaLibrary } from "./MediaLibraryContext"

export function MediaLibraryToolbar() {
  const { media, mediaTotal } = useMediaLibrary()
  const selectionManager = useSelectionManager()

  return (
    <Box alignItems="center" gap={16}>
      {selectionManager.isSelectAllEnabled && (
        <Checkbox
          onCheckedChange={selectionManager.toggleSelectAll}
          checked={selectionManager.selectedCount > 0}
          indeterminate={selectionManager.selectedCount !== mediaTotal}
          onClick={(event) => event.stopPropagation()}
          disabled={media.length === 0}
        />
      )}
      {media.length !== 0 && <MediaLibraryAssetCount count={mediaTotal} />}
      <Box gap={8} alignItems="center">
        <Text>Filter:</Text>
        <AssetTypeFilter />
        <TagsFilter />
      </Box>
    </Box>
  )
}

interface AssetTypeMetadata {
  name?: string
  icon: IconName
}

const assetTypesMetadata: Record<MediaAssetType, AssetTypeMetadata> = {
  all: { name: "All media", icon: "public" },
  image: { name: "Images", icon: "image" },
  video: { name: "Videos", icon: "videocam" },
  document: { name: "Documents", icon: "insertDriveFile" },
  audio: { name: "Audio files", icon: "musicNote" }
}

const assetTypesMetadataEntries = Object.entries(assetTypesMetadata) as [
  MediaAssetType,
  AssetTypeMetadata
][]

function AssetTypeFilter() {
  const { assetType, initialAssetType, onFilterChange } = useMediaLibrary()

  const [optimisticAssetType, setOptimisticAssetType] = useState(assetType)
  useOnChange(assetType, () => {
    setOptimisticAssetType(assetType)
  })

  const [dropdownOpen, setDropdownOpen] = useState(false)

  function onAssetTypeToggle(newAssetType: MediaAssetType) {
    setDropdownOpen(false)
    setOptimisticAssetType(newAssetType)
    onFilterChange({ assetType: newAssetType })
  }

  const { icon, name } = assetTypesMetadata[optimisticAssetType]
  const tooltipAssetTypeName = assetTypesMetadata[initialAssetType].name
  const isDropdownDisabled = initialAssetType !== "all"

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger disabled={isDropdownDisabled}>
        <AssetTypeFilterDropdownButton
          icon={icon}
          isDropdownDisabled={isDropdownDisabled}
          buttonName={name}
          tooltipAssetTypeName={tooltipAssetTypeName}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent minWidth={256} align="start" childrenFocusScope>
        <>
          {assetTypesMetadataEntries
            .filter(
              ([dropdownAssetType]) => dropdownAssetType !== optimisticAssetType
            )
            .map(([dropdownAssetType, { icon, name }]) => (
              <DropdownMenuItem
                key={dropdownAssetType}
                startIcon={<Icon name={icon} />}
                onSelect={() => onAssetTypeToggle(dropdownAssetType)}
              >
                {name}
              </DropdownMenuItem>
            ))}
        </>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface AssetTypeFilterDropdownButtonProps {
  icon: IconName
  isDropdownDisabled: boolean
  buttonName?: string
  tooltipAssetTypeName?: string
}

const AssetTypeFilterDropdownButton = forwardRef<
  HTMLButtonElement,
  AssetTypeFilterDropdownButtonProps
>(function AssetTypeFilterDropdownButton(props, ref) {
  const {
    icon,
    isDropdownDisabled,
    buttonName,
    tooltipAssetTypeName,
    ...rest
  } = props

  return (
    <Tooltip
      content={`You can only pick ${
        tooltipAssetTypeName?.toLocaleLowerCase() ?? "this type of media"
      }`}
      visible={isDropdownDisabled}
    >
      <Button
        startIcon={icon}
        endIcon="arrowDropDown"
        size="small"
        color="grey"
        disabled={isDropdownDisabled}
        ref={ref}
        // rest added to make the grandparent DropdownMenuTrigger work with asChild
        {...rest}
      >
        {buttonName}
      </Button>
    </Tooltip>
  )
})

interface SelectedTagsListProps {
  optimisticTagIds: string[]
  setOptimisticTagIds: (tagIds: string[]) => void
}

function SelectedTagsList(props: SelectedTagsListProps) {
  const { optimisticTagIds, setOptimisticTagIds } = props

  const { onFilterChange } = useMediaLibrary()

  const { tags } = useTagSearch()

  const tagsMap = useMemo(() => groupBy(tags, (tag) => tag.id), [tags])

  function onRemoveTag(tag: Tag) {
    const newTagIds = optimisticTagIds.filter((tagId) => tagId !== tag.id)
    setOptimisticTagIds(newTagIds)
    onFilterChange({ tagIds: newTagIds })
  }

  return (
    <>
      {optimisticTagIds.map((tagId) => {
        const tag = tagsMap.get(tagId)?.[0]
        if (!tag) return null

        return (
          <Badge
            key={tag.id}
            title={tag.name}
            size="medium"
            color="purple"
            onClose={() => onRemoveTag(tag)}
            selected
          />
        )
      })}
    </>
  )
}

function TagsFilter() {
  const { setTagManagerOpen, tagIds, onFilterChange } = useMediaLibrary()

  const [tagSearchTerm, setTagSearchTerm] = useState("")
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false)
  const [optimisticTagIds, setOptimisticTagIds] = useState(tagIds)

  useOnChange(JSON.stringify(tagIds), () => {
    setOptimisticTagIds(tagIds)
  })

  function onOpenChange(newOpen: boolean) {
    setTagDropdownOpen(newOpen)
    if (!newOpen) setTagSearchTerm("")
  }

  function onTagToggle(tag: Tag) {
    const newTagIds = optimisticTagIds.includes(tag.id)
      ? tagIds.filter((tagId) => tagId !== tag.id)
      : [...tagIds, tag.id]
    setOptimisticTagIds(newTagIds)
    onFilterChange({ tagIds: newTagIds })
    setTagDropdownOpen(false)
  }

  function onManageTags() {
    setTagDropdownOpen(false)
    setTagManagerOpen(true)
  }

  return (
    <>
      <DropdownMenu open={tagDropdownOpen} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger>
          <Button
            startIcon="label"
            endIcon="arrowDropDown"
            size="small"
            color="grey"
          >
            Tags
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          minWidth={256}
          align="start"
          border={false}
          childrenFocusScope
        >
          <ComboBox variant="attached">
            <ComboBoxInput
              value={tagSearchTerm}
              onValueChange={setTagSearchTerm}
              placeholder="Search for a tag"
              // The combobox requires the input to be focused in order for
              // it to be keyboard navigateable.
              // setting autofocus means on open of the dropdown menu, we immediately
              // focus into the combobox so that users can keyboard navigate
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <ComboBoxContent>
              <ErrorBoundary
                renderError={() => (
                  <Text
                    color="grey9"
                    variant="small"
                    sx={{ marginBlock: 8, marginInline: 8 }}
                  >
                    Error getting tags
                  </Text>
                )}
              >
                <Suspense
                  fallback={
                    <Box padding={8} flexDirection="column" gap={8}>
                      {range(1, 6).map((num) => (
                        <Skeleton key={num} height={24} width={85} />
                      ))}
                    </Box>
                  }
                >
                  <TagList
                    searchTerm={tagSearchTerm}
                    setSearchTerm={setTagSearchTerm}
                    onTagToggle={onTagToggle}
                    optimisticTagIds={optimisticTagIds}
                  />
                </Suspense>
              </ErrorBoundary>
            </ComboBoxContent>
            <ComboboxAction>
              <Button
                invisible
                startIcon="edit"
                textWeight="normal"
                color="dark"
                onClick={onManageTags}
              >
                Manage tags
              </Button>
            </ComboboxAction>
          </ComboBox>
        </DropdownMenuContent>
      </DropdownMenu>
      {optimisticTagIds.length > 0 && (
        <ErrorBoundary>
          <Suspense>
            <SelectedTagsList
              optimisticTagIds={optimisticTagIds}
              setOptimisticTagIds={setOptimisticTagIds}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  )
}

interface TagListProps {
  onTagToggle: (tag: Tag) => void
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  optimisticTagIds: string[]
}

function TagList(props: TagListProps) {
  const { onTagToggle, searchTerm, optimisticTagIds } = props

  const { tags } = useTagSearch()

  const filteredTags = useMemo(() => {
    const searchTermLowerCase = searchTerm.toLowerCase()
    return tags.filter((tag) =>
      tag.name.toLowerCase().includes(searchTermLowerCase)
    )
  }, [tags, searchTerm])

  return (
    <>
      {!filteredTags.length && (
        <Text
          color="grey9"
          variant="small"
          sx={{ marginBlock: 8, marginInline: 8 }}
        >
          No tags found
        </Text>
      )}
      {filteredTags.map((tag, index) => {
        const checked = optimisticTagIds.includes(tag.id)
        return (
          <ComboBoxItem
            key={tag.id}
            value={tag.id}
            onCheckedChange={() => onTagToggle(tag)}
            checked={checked}
          >
            <Badge
              title={tag.name}
              color="purple"
              sx={{
                marginLeft: 8,
                marginBottom: index === filteredTags.length - 1 ? 8 : 4,
                marginTop: index === 0 ? 8 : 4
              }}
              size="medium"
            />
          </ComboBoxItem>
        )
      })}
    </>
  )
}
